// PRD 08 §3.1 — Disease Group add/edit slide-panel (520px).
// Hybrid range input: selecting an ICD-10 chapter auto-fills rangeStart/rangeEnd
// and the group name (only if the name is empty). All fields stay manually
// editable after the auto-fill — the chapter is not persisted.
// Cancel: dirty-check + discard confirm, matching the medical-staff invite panel.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { hasEmoji } from '@/shared/utils/emoji';

import {
  useCreateDiseaseGroup,
  useUpdateDiseaseGroup,
} from '../hooks/use-disease-code-mutations';
import type { DiseaseGroup } from '../types/disease-code.types';
import { DiseaseCodeApiError } from '../types/disease-code.types';
import { getDiseaseCodeErrorMessage } from '../utils/error-message';
import { ICD_CHAPTERS } from '../utils/icd-chapters';
import { IcdChapterSelect } from './IcdChapterSelect';

interface DiseaseGroupFormPanelProps {
  open: boolean;
  // Null = create mode; group = edit mode.
  group: DiseaseGroup | null;
  onClose: () => void;
}

type FieldKey = 'name' | 'chapter' | 'rangeStart' | 'rangeEnd' | 'description';

interface FormState {
  name: string;
  chapter: string; // single letter or ''
  rangeStart: string;
  rangeEnd: string;
  description: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  chapter: '',
  rangeStart: '',
  rangeEnd: '',
  description: '',
};

// FEATURE_031 group-name charset (DH-DISEASE-CHARSET-001, option A): 질병군명 is Hangul + Latin letter
// (a-z A-Z) + digit + space PLUS the middle dot (·, U+00B7) and parentheses used by the ICD-10 chapter
// labels the form auto-fills (e.g. 신생물(종양), 내분비·영양·대사 질환) — a whitelist matching the BE @Pattern.
// Wider than the disease-code name. Name fails when it does NOT match this pattern.
const NAME_CHARSET_RE = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9·()]+( [가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9·()]+)*$/;

function formFromGroup(group: DiseaseGroup | null): FormState {
  if (!group) return EMPTY_FORM;
  // Pre-select the chapter whose band CONTAINS this group's range. The chapter is
  // UI-only (PRD §4.1) and re-derived on every open, so a manually narrowed range
  // (e.g. A00~B99 → A20~B80) must still resolve to its parent chapter. Validation
  // (RANGE_OUT_OF_CHAPTER) guarantees a saved range stays inside a single, non-
  // overlapping band, so containment is unambiguous. Exact-band ranges are a subset
  // of this. Ranges fitting no band (legacy/odd data) leave the dropdown unselected.
  const chapter = ICD_CHAPTERS.find(
    (c) => group.rangeStart >= c.rangeStart && group.rangeEnd <= c.rangeEnd,
  );
  return {
    name: group.name,
    chapter: chapter?.letter ?? '',
    rangeStart: group.rangeStart,
    rangeEnd: group.rangeEnd,
    description: group.description ?? '',
  };
}

export function DiseaseGroupFormPanel({ open, group, onClose }: DiseaseGroupFormPanelProps) {
  const { t } = useTranslation('admin');
  const isEdit = group !== null;

  const createMutation = useCreateDiseaseGroup();
  const updateMutation = useUpdateDiseaseGroup();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  // Synchronous double-submit guard — isPending (React state) disables the
  // button only on the next render, so a fast double-click fires two mutations
  // (two error toasts) before it applies. A ref blocks the second call at once.
  const submittingRef = useRef(false);

  // Offline lock: a save can't complete without the network, so after an offline
  // attempt we surface the error and disable Save until the connection returns.
  const [offlineLocked, setOfflineLocked] = useState(false);
  useEffect(() => {
    const unlock = () => setOfflineLocked(false);
    window.addEventListener('online', unlock);
    return () => window.removeEventListener('online', unlock);
  }, []);

  // Repopulate the form whenever the slide-panel becomes visible with a
  // (possibly different) target group. The empty mode resets to EMPTY_FORM.
  useEffect(() => {
    if (!open) return;
    setForm(formFromGroup(group));
    setErrors({});
    setIsDirty(false);
    setShowDiscard(false);
    setOfflineLocked(false);
  }, [open, group]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    if (key in errors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as FieldKey];
        return next;
      });
    }
  };

  // PRD §3.1 hybrid input: selecting a chapter rewrites rangeStart/rangeEnd
  // and fills the group name ONLY when it's currently empty (preserve user
  // input). Manual edits afterward are allowed.
  const handleChapterChange = (letter: string) => {
    setForm((f) => {
      if (!letter) return { ...f, chapter: '' };
      const ch = ICD_CHAPTERS.find((c) => c.letter === letter);
      if (!ch) return { ...f, chapter: letter };
      const chapterLabel = t(`diseaseCodeManagement.icdChapters.${letter}`);
      return {
        ...f,
        chapter: letter,
        rangeStart: ch.rangeStart,
        rangeEnd: ch.rangeEnd,
        name: f.name.trim() ? f.name : chapterLabel,
      };
    });
    setIsDirty(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.rangeStart;
      delete next.rangeEnd;
      delete next.name;
      delete next.chapter;
      return next;
    });
  };

  const validate = (): {
    ok: boolean;
    name: string;
    rangeStart: string;
    rangeEnd: string;
    description: string;
  } => {
    const next: Partial<Record<FieldKey, string>> = {};
    const name = form.name.trim();
    // PRD 08 §4.1 — name required, 1–16 chars; charset = Hangul + Latin letter + digit + single space (DH-DISEASE-CHARSET-001).
    if (!name) {
      next.name = t('diseaseCodeManagement.errors.NAME_REQUIRED');
    } else if (name.length > 16) {
      next.name = t('diseaseCodeManagement.errors.DISEASE_GROUP_NAME_TOO_LONG');
    } else if (!NAME_CHARSET_RE.test(name)) {
      next.name = t('diseaseCodeManagement.errors.DISEASE_GROUP_NAME_INVALID_CHAR');
    }
    // PRD 08 §3.1 — ICD-10 chapter selection is required (Req Clarification Q1).
    if (!form.chapter) {
      next.chapter = t('diseaseCodeManagement.errors.CHAPTER_REQUIRED');
    }
    const rangeStart = form.rangeStart.trim().toUpperCase();
    const rangeEnd = form.rangeEnd.trim().toUpperCase();
    if (!rangeStart) next.rangeStart = t('diseaseCodeManagement.errors.RANGE_REQUIRED');
    if (!rangeEnd) next.rangeEnd = t('diseaseCodeManagement.errors.RANGE_REQUIRED');
    // Lexicographic check only — ICD-10 format / overlap belong to the BE.
    if (rangeStart && rangeEnd && rangeStart > rangeEnd) {
      next.rangeStart = t('diseaseCodeManagement.errors.RANGE_INVALID');
    }
    // PRD 08 §4.1 (BA decision B) — the saved range must stay within the selected
    // ICD-10 chapter's band. The chapter is not persisted, but a range outside it
    // produces inconsistent classification. Compare lexicographically (codes are
    // uppercased letter+digits). Only when ordering is valid, to avoid a double
    // error on the same field.
    if (form.chapter && rangeStart && rangeEnd && rangeStart <= rangeEnd) {
      const ch = ICD_CHAPTERS.find((c) => c.letter === form.chapter);
      if (ch) {
        if (rangeStart < ch.rangeStart || rangeStart > ch.rangeEnd) {
          next.rangeStart = t('diseaseCodeManagement.errors.RANGE_OUT_OF_CHAPTER');
        }
        if (rangeEnd < ch.rangeStart || rangeEnd > ch.rangeEnd) {
          next.rangeEnd = t('diseaseCodeManagement.errors.RANGE_OUT_OF_CHAPTER');
        }
      }
    }
    // PRD 08 §4.1 — description optional, max 50 chars (특수문자·줄바꿈 허용).
    // SHCT-854 — 일반 특수문자만 허용이라는 뜻이며, 이모지는 [공통] 00 §4.2에 따라 전 클래스
    // 불허다. 질병군명은 이미 charset 화이트리스트로 막혀 있어 설명만 통과하고 있었다.
    const description = form.description.trim();
    if (description.length > 50) {
      next.description = t('diseaseCodeManagement.errors.DISEASE_GROUP_DESCRIPTION_TOO_LONG');
    } else if (hasEmoji(description)) {
      next.description = t('common:errors.emojiNotAllowed');
    }
    setErrors(next);
    return {
      ok: Object.keys(next).length === 0,
      name,
      rangeStart,
      rangeEnd,
      // DC-3 — send the trimmed string ('' when cleared) so the BE clears the
      // description instead of ignoring a null. BE normalizes '' → null on save.
      description,
    };
  };

  // Apply a `DiseaseCodeApiError` from BE/mock to the inline field state.
  // Per CLAUDE.md §7.1, validation / form-context business-logic errors show
  // inline only (no toast); the toast is reserved for system errors that carry
  // no field. RANGE_OVERLAP is a chapter-level conflict, so it pins to the
  // ICD-10 챕터 field regardless of which field the server flagged.
  const applyApiError = (e: unknown) => {
    const localized = getDiseaseCodeErrorMessage(
      e,
      t,
      'diseaseCodeManagement.toasts.saveError',
    );
    let inlineShown = false;
    if (e instanceof DiseaseCodeApiError && e.fields) {
      const next: Partial<Record<FieldKey, string>> = {};
      for (const k of Object.keys(e.fields)) {
        const field = e.code === 'RANGE_OVERLAP' ? 'chapter' : k;
        if (
          field === 'name' ||
          field === 'chapter' ||
          field === 'rangeStart' ||
          field === 'rangeEnd' ||
          field === 'description'
        ) {
          next[field as FieldKey] = localized;
        }
      }
      if (Object.keys(next).length > 0) {
        setErrors((prev) => ({ ...prev, ...next }));
        inlineShown = true;
      }
    }
    if (!inlineShown) {
      toast.error(localized);
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current || isPending) return;
    const v = validate();
    if (!v.ok) return;

    // Offline: don't fire a request that would hang the panel. Show the network
    // error and lock Save until we're back online (the 'online' listener unlocks).
    if (!navigator.onLine) {
      toast.error(t('diseaseCodeManagement.errors.NETWORK'));
      setOfflineLocked(true);
      return;
    }

    submittingRef.current = true;
    try {
      if (isEdit && group) {
        await updateMutation.mutateAsync({
          id: group.id,
          input: {
            name: v.name,
            rangeStart: v.rangeStart,
            rangeEnd: v.rangeEnd,
            description: v.description,
          },
        });
        toast.success(t('diseaseCodeManagement.toasts.groupUpdated'));
      } else {
        await createMutation.mutateAsync({
          name: v.name,
          rangeStart: v.rangeStart,
          rangeEnd: v.rangeEnd,
          description: v.description,
        });
        toast.success(t('diseaseCodeManagement.toasts.groupAdded'));
      }
      onClose();
    } catch (e) {
      applyApiError(e);
      // Connection dropped mid-request (offline flag didn't catch it): lock Save
      // until back online, matching the offline pre-check above.
      if (e instanceof DiseaseCodeApiError && e.code === 'NETWORK') {
        setOfflineLocked(true);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const handleClose = () => {
    if (isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    onClose();
  };

  return (
    <>
      <SlidePanel
        open={open}
        width={520}
        title={t(
          isEdit
            ? 'diseaseCodeManagement.groupForm.titleEdit'
            : 'diseaseCodeManagement.groupForm.titleAdd',
        )}
        onClose={handleClose}
      >
        <div className="form-field">
          <label className="form-field__label" htmlFor="dcGroupName">
            {t('diseaseCodeManagement.groupForm.fields.name.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            id="dcGroupName"
            type="text"
            className="form-field__input"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder={t('diseaseCodeManagement.groupForm.fields.name.placeholder')}
          />
          {errors.name ? (
            <p className="form-field__error">{errors.name}</p>
          ) : null}
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="dcChapter">
            {t('diseaseCodeManagement.groupForm.fields.chapter.label')}{' '}
            <span className="required">*</span>
          </label>
          <IcdChapterSelect id="dcChapter" value={form.chapter} onChange={handleChapterChange} />
          {errors.chapter ? (
            <p className="form-field__error">{errors.chapter}</p>
          ) : null}
          <p
            style={{
              fontSize: 11,
              color: 'var(--muted-foreground)',
              marginTop: 4,
            }}
          >
            {t('diseaseCodeManagement.groupForm.fields.chapter.helper')}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-field__label" htmlFor="dcRangeStart">
              {t('diseaseCodeManagement.groupForm.fields.rangeStart.label')}{' '}
              <span className="required">*</span>
            </label>
            <input
              id="dcRangeStart"
              type="text"
              className="form-field__input"
              value={form.rangeStart}
              onChange={(e) => update('rangeStart', e.target.value)}
              placeholder={t('diseaseCodeManagement.groupForm.fields.rangeStart.placeholder')}
              maxLength={10}
            />
            {errors.rangeStart ? (
              <p className="form-field__error">{errors.rangeStart}</p>
            ) : null}
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-field__label" htmlFor="dcRangeEnd">
              {t('diseaseCodeManagement.groupForm.fields.rangeEnd.label')}{' '}
              <span className="required">*</span>
            </label>
            <input
              id="dcRangeEnd"
              type="text"
              className="form-field__input"
              value={form.rangeEnd}
              onChange={(e) => update('rangeEnd', e.target.value)}
              placeholder={t('diseaseCodeManagement.groupForm.fields.rangeEnd.placeholder')}
              maxLength={10}
            />
            {errors.rangeEnd ? (
              <p className="form-field__error">{errors.rangeEnd}</p>
            ) : null}
          </div>
        </div>

        <div className="form-field" style={{ marginTop: 16 }}>
          <label className="form-field__label" htmlFor="dcGroupDesc">
            {t('diseaseCodeManagement.groupForm.fields.description.label')}
          </label>
          <textarea
            id="dcGroupDesc"
            className="form-field__textarea"
            rows={4}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder={t('diseaseCodeManagement.groupForm.fields.description.placeholder')}
          />
          {errors.description ? (
            <p className="form-field__error">{errors.description}</p>
          ) : null}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={handleClose}
            disabled={isPending}
          >
            {t('diseaseCodeManagement.groupForm.actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={handleSubmit}
            disabled={isPending || offlineLocked}
          >
            {t('diseaseCodeManagement.groupForm.actions.save')}
          </button>
        </div>
      </SlidePanel>

      <ConfirmModal
        open={showDiscard}
        title={t('diseaseCodeManagement.modals.discard.title')}
        message={t('diseaseCodeManagement.modals.discard.message')}
        confirmLabel={t('diseaseCodeManagement.modals.discard.confirm')}
        cancelLabel={t('diseaseCodeManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
}
